import React, { useState, useEffect, useCallback } from 'react';
import { 
  Box, 
  Typography, 
  CircularProgress, 
  Alert, 
  Select, 
  MenuItem, 
  FormControl, 
  InputLabel, 
  Button, 
  TextField,
  Grid,
  Card,
  CardContent,
  Collapse,
  IconButton,
  Chip,
  useTheme,
  alpha
} from '@mui/material';
import { 
  ExpandMore as ExpandMoreIcon,
  FilterList as FilterListIcon,
  Refresh as RefreshIcon,
  Map as MapIcon,
  CloudUpload as CloudUploadIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useAuth0 } from '@auth0/auth0-react';
import MapComponent from './MapComponent';
import SelectedAccordsGrid from './SelectedAccordsGrid';
import { getUmapEmbeddings, getFilters, getUploadedCaseData, uploadCaseFile } from '../services/api';
import { similarCasesToAccords } from '../services/similarCases';

function Home({ selectedAccords, onAccordClick, onRemoveAccord, onPreselectAccords, accordLimitAlert }) {
  const { isAuthenticated } = useAuth0();
  const [accords, setAccords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterOptions, setFilterOptions] = useState({});
  const [filtersExpanded, setFiltersExpanded] = useState(false);
  // Add caching for uploaded case data to prevent redundant API calls
  const [uploadedCaseData, setUploadedCaseData] = useState(null);
  const [uploadedCaseChecked, setUploadedCaseChecked] = useState(false);
  const theme = useTheme();
  const [selectedFilters, setSelectedFilters] = useState({
    advogados: [],
    recorrentes: [],
    relatores: [],
    resultados: [],
    period_start: null,
    period_end: null,
    titulo_keyword: '',
    ementa_keyword: '',
    ementa_search_mode: 'Correspondência exata',
  });

  // Cached function to fetch uploaded case data and prevent redundant API calls
  const fetchUploadedCaseData = useCallback(async (force = false) => {
    if (!force && uploadedCaseChecked) {
      return uploadedCaseData;
    }
    
    try {
      const data = await getUploadedCaseData();
      setUploadedCaseData(data);
      setUploadedCaseChecked(true);
      return data;
    } catch (error) {
      console.error('Error fetching uploaded case data:', error);
      setUploadedCaseChecked(true);
      return null;
    }
  }, [uploadedCaseChecked, uploadedCaseData]);

  useEffect(() => {
    const fetchInitialData = async () => {
      try {
        setLoading(true);
        setError(null);

        console.log('Starting initial data fetch...');

        // Fetch filter options first
        console.log('Fetching filter options...');
        const filtersData = await getFilters();
        console.log('Filter options received:', filtersData);
        setFilterOptions(filtersData);

        // Set default date filter for the last 12 months
        const today = new Date();
        const twelveMonthsAgo = new Date();
        twelveMonthsAgo.setFullYear(today.getFullYear() - 1);

        const defaultPeriodStart = twelveMonthsAgo.toISOString().split('T')[0];
        const defaultPeriodEnd = today.toISOString().split('T')[0];

        const defaultFilters = {
          advogados: [],
          recorrentes: [],
          relatores: [],
          resultados: [],
          period_start: defaultPeriodStart,
          period_end: defaultPeriodEnd,
          titulo_keyword: '',
          ementa_keyword: '',
          ementa_search_mode: 'Correspondência exata',
        };

        setSelectedFilters(defaultFilters);

        // Fetch accords with default filters
        console.log('Fetching initial accords with filters:', defaultFilters);
  const initialAccords = await getUmapEmbeddings(defaultFilters);
        console.log('Initial Accords Raw Data received:', {
          filtered_data_ids_count: initialAccords.filtered_data_ids?.length,
          embeddings_2D_x_count: initialAccords.embeddings_2D?.x?.length,
          embeddings_2D_y_count: initialAccords.embeddings_2D?.y?.length,
          hover_data_keys: Object.keys(initialAccords.hover_data || {}),
        });
        
        // Validate response structure
        if (!initialAccords.filtered_data_ids || !initialAccords.embeddings_2D || !initialAccords.hover_data) {
          throw new Error('Invalid response structure from backend');
        }

        let transformedAccords = initialAccords.filtered_data_ids.map((id, index) => ({
          id: id,
          x: initialAccords.embeddings_2D.x[index],
          y: initialAccords.embeddings_2D.y[index],
          result: initialAccords.hover_data.acordao_resultado[index],
          ementa: initialAccords.hover_data.acordao_ementa[index],
          penalidade: initialAccords.hover_data.acordao_penalidade[index],
          baseLegal: initialAccords.hover_data.acordao_base[index],
          // Add additional fields for tooltip
          ano: initialAccords.hover_data.ano_sessoes?.[index],
          sessao: initialAccords.hover_data.sessao_nr_int?.[index],
          processo: initialAccords.hover_data.acordao_titulo?.[index],
        }));
        // Attempt to overlay uploaded case from session, if any
        const uploaded = await fetchUploadedCaseData();
        if (uploaded && uploaded.filtered_data_ids && uploaded.embeddings_2D && uploaded.hover_data) {
          const idx = 0; // backend returns a single point
          transformedAccords = [
            ...transformedAccords,
            {
              id: uploaded.filtered_data_ids[idx],
              x: uploaded.embeddings_2D.x[idx],
              y: uploaded.embeddings_2D.y[idx],
              result: uploaded.hover_data.acordao_resultado?.[idx] ?? 'Carregado pelo usuário',
              ementa: uploaded.hover_data.acordao_ementa?.[idx] ?? 'Carregado pelo usuário',
              penalidade: uploaded.hover_data.acordao_penalidade?.[idx] ?? 'Carregado pelo usuário',
              baseLegal: uploaded.hover_data.acordao_base?.[idx] ?? 'Carregado pelo usuário',
              ano: uploaded.hover_data.ano_sessoes?.[idx] ?? 'Carregado pelo usuário',
              sessao: uploaded.hover_data.sessao_nr_int?.[idx] ?? 'Carregado pelo usuário',
              processo: uploaded.hover_data.acordao_titulo?.[idx] ?? 'Carregado pelo usuário',
              uploadedCase: true,
            }
          ];
        }

        console.log(`Successfully transformed ${transformedAccords.length} accords`);
        setAccords(transformedAccords);

      } catch (err) {
        console.error('Failed to fetch initial data:', err);
        
        // Provide more specific error messages
        if (err.message?.includes('timeout')) {
          setError('The server is taking too long to respond. This may be due to the Cloud Run instance starting up. Please wait a moment and try again.');
        } else if (err.message?.includes('Server error')) {
          setError('The backend server is experiencing issues. Please try again in a few minutes.');
        } else if (err.message?.includes('Network Error')) {
          setError('Network connection error. Please check your internet connection and try again.');
        } else {
          setError(`Failed to fetch initial data: ${err.message || 'Unknown error occurred'}. Please try refreshing the page.`);
        }
      } finally {
        setLoading(false);
      }
    };

    if (isAuthenticated) {
      fetchInitialData();
    }
  }, [isAuthenticated, fetchUploadedCaseData]); // Add fetchUploadedCaseData dependency

  const handleFilterChange = (event) => {
    const { name, value } = event.target;
    setSelectedFilters(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleDateChange = (date, name) => {
    setSelectedFilters(prev => ({
      ...prev,
      [name]: date ? date.toISOString().split('T')[0] : null,
    }));
  };

  const applyFilters = async () => {
    try {
      setLoading(true);
      setError(null);
      
      console.log('Applying filters:', selectedFilters);
      
      const data = await getUmapEmbeddings(selectedFilters);
      
      console.log('Filter response received:', {
        filtered_data_ids_count: data.filtered_data_ids?.length,
        embeddings_2D_x_count: data.embeddings_2D?.x?.length,
        embeddings_2D_y_count: data.embeddings_2D?.y?.length,
        hover_data_keys: Object.keys(data.hover_data || {}),
      });

      // Validate response structure
      if (!data.filtered_data_ids || !data.embeddings_2D || !data.hover_data) {
        throw new Error('Invalid response structure from backend');
      }

      let transformedAccords = data.filtered_data_ids.map((id, index) => ({
        id: id,
        x: data.embeddings_2D.x[index],
        y: data.embeddings_2D.y[index],
        result: data.hover_data.acordao_resultado[index],
        ementa: data.hover_data.acordao_ementa[index],
        penalidade: data.hover_data.acordao_penalidade[index],
        baseLegal: data.hover_data.acordao_base[index],
        // Add additional fields for tooltip
        ano: data.hover_data.ano_sessoes?.[index],
        sessao: data.hover_data.sessao_nr_int?.[index],
        processo: data.hover_data.acordao_titulo?.[index],
      }));
      // Overlay uploaded case from session if present
      const uploaded = await fetchUploadedCaseData();
      if (uploaded && uploaded.filtered_data_ids && uploaded.embeddings_2D && uploaded.hover_data) {
        const idx = 0;
        transformedAccords = [
          ...transformedAccords,
          {
            id: uploaded.filtered_data_ids[idx],
            x: uploaded.embeddings_2D.x[idx],
            y: uploaded.embeddings_2D.y[idx],
            result: uploaded.hover_data.acordao_resultado?.[idx] ?? 'Carregado pelo usuário',
            ementa: uploaded.hover_data.acordao_ementa?.[idx] ?? 'Carregado pelo usuário',
            penalidade: uploaded.hover_data.acordao_penalidade?.[idx] ?? 'Carregado pelo usuário',
            baseLegal: uploaded.hover_data.acordao_base?.[idx] ?? 'Carregado pelo usuário',
            ano: uploaded.hover_data.ano_sessoes?.[idx] ?? 'Carregado pelo usuário',
            sessao: uploaded.hover_data.sessao_nr_int?.[idx] ?? 'Carregado pelo usuário',
            processo: uploaded.hover_data.acordao_titulo?.[idx] ?? 'Carregado pelo usuário',
            uploadedCase: true,
          }
        ];
      }
      
      console.log(`Successfully applied filters and transformed ${transformedAccords.length} accords`);
      setAccords(transformedAccords);
    } catch (err) {
      console.error('Failed to apply filters:', err);
      
      // Provide more specific error messages
      if (err.message?.includes('timeout')) {
        setError('The filtering operation timed out. The dataset may be large or the server is busy. Please try with more restrictive filters or try again later.');
      } else if (err.message?.includes('Server error')) {
        setError('The backend server encountered an error while processing your filters. Please try again or adjust your filter criteria.');
      } else {
        setError(`Failed to apply filters: ${err.message || 'Unknown error occurred'}. Please try again.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const refreshData = async () => {
    try {
      setLoading(true);
      setError(null);
      console.log('Refreshing data with current filters:', selectedFilters);
      
      const data = await getUmapEmbeddings(selectedFilters);
      
      // Validate response structure
      if (!data.filtered_data_ids || !data.embeddings_2D || !data.hover_data) {
        throw new Error('Invalid response structure from backend');
      }

      let transformedAccords = data.filtered_data_ids.map((id, index) => ({
        id: id,
        x: data.embeddings_2D.x[index],
        y: data.embeddings_2D.y[index],
        result: data.hover_data.acordao_resultado[index],
        ementa: data.hover_data.acordao_ementa[index],
        penalidade: data.hover_data.acordao_penalidade[index],
        baseLegal: data.hover_data.acordao_base[index],
        // Add additional fields for tooltip
        ano: data.hover_data.ano_sessoes?.[index],
        sessao: data.hover_data.sessao_nr_int?.[index],
        processo: data.hover_data.acordao_titulo?.[index],
      }));
      // Overlay uploaded case from session if present
      const uploaded = await fetchUploadedCaseData();
      if (uploaded && uploaded.filtered_data_ids && uploaded.embeddings_2D && uploaded.hover_data) {
        const idx = 0;
        transformedAccords = [
          ...transformedAccords,
          {
            id: uploaded.filtered_data_ids[idx],
            x: uploaded.embeddings_2D.x[idx],
            y: uploaded.embeddings_2D.y[idx],
            result: uploaded.hover_data.acordao_resultado?.[idx] ?? 'Carregado pelo usuário',
            ementa: uploaded.hover_data.acordao_ementa?.[idx] ?? 'Carregado pelo usuário',
            penalidade: uploaded.hover_data.acordao_penalidade?.[idx] ?? 'Carregado pelo usuário',
            baseLegal: uploaded.hover_data.acordao_base?.[idx] ?? 'Carregado pelo usuário',
            ano: uploaded.hover_data.ano_sessoes?.[idx] ?? 'Carregado pelo usuário',
            sessao: uploaded.hover_data.sessao_nr_int?.[idx] ?? 'Carregado pelo usuário',
            processo: uploaded.hover_data.acordao_titulo?.[idx] ?? 'Carregado pelo usuário',
            uploadedCase: true,
          }
        ];
      }
      
      console.log(`Successfully refreshed ${transformedAccords.length} accords`);
      setAccords(transformedAccords);
    } catch (err) {
      console.error('Failed to refresh data:', err);
      
      if (err.message?.includes('timeout')) {
        setError('The refresh operation timed out. Please try again in a moment.');
      } else if (err.message?.includes('Server error')) {
        setError('The backend server is having issues. Please try again later.');
      } else {
        setError(`Failed to refresh data: ${err.message || 'Unknown error occurred'}.`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* Header Section */}
      <Box sx={{ mb: 4 }}>
        <Typography 
          variant="h3" 
          sx={{ 
            fontWeight: 700,
            mb: 1,
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
            backgroundClip: 'text',
            textFillColor: 'transparent',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            display: 'flex',
            alignItems: 'center',
            gap: 2,
          }}
        >
          <MapIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
          Mapa de Acórdãos
        </Typography>
        <Typography variant="subtitle1" color="text.secondary">
          Este mapa permite visualizar e selecionar decisões do CRSNSP para análise. Os acórdãos selecionados aparecerão no grid abaixo do mapa.
        </Typography>
      </Box>

      {/* Filter Section */}
      {isAuthenticated && filterOptions && (
        <Card sx={{ mb: 3, overflow: 'visible' }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <FilterListIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Filtros de Pesquisa
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <IconButton 
                  onClick={() => setFiltersExpanded(!filtersExpanded)}
                  sx={{
                    transform: filtersExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.3s ease',
                  }}
                >
                  <ExpandMoreIcon />
                </IconButton>
              </Box>
            </Box>

            <Collapse in={filtersExpanded}>
              <Box sx={{ mt: 1 }}>
                {/* Filtros principais (Advogados, Recorrentes, Relatores, Resultados) */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={6} sm={3}>
                    <FormControl fullWidth sx={{ minWidth: 200 }}>
                      <InputLabel id="advogados-label">Advogados</InputLabel>
                      <Select
                        labelId="advogados-label"
                        multiple
                        name="advogados"
                        value={selectedFilters.advogados}
                        onChange={handleFilterChange}
                        label="Advogados"
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {filterOptions.advogados && filterOptions.advogados.map((option) => (
                          <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                  
                  <Grid item xs={6} sm={3}>
                    <FormControl fullWidth sx={{ minWidth: 200 }}>
                      <InputLabel id="recorrentes-label">Recorrentes</InputLabel>
                      <Select
                        labelId="recorrentes-label"
                        multiple
                        name="recorrentes"
                        value={selectedFilters.recorrentes}
                        onChange={handleFilterChange}
                        label="Recorrentes"
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {filterOptions.recorrentes && filterOptions.recorrentes.map((option) => (
                          <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <FormControl fullWidth sx={{ minWidth: 200 }}>
                      <InputLabel id="relatores-label">Relatores</InputLabel>
                      <Select
                        labelId="relatores-label"
                        multiple
                        name="relatores"
                        value={selectedFilters.relatores}
                        onChange={handleFilterChange}
                        label="Relatores"
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {filterOptions.relatores && filterOptions.relatores.map((option) => (
                          <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>

                  <Grid item xs={6} sm={3}>
                    <FormControl fullWidth sx={{ minWidth: 200 }}>
                      <InputLabel id="resultados-label">Resultados</InputLabel>
                      <Select
                        labelId="resultados-label"
                        multiple
                        name="resultados"
                        value={selectedFilters.resultados}
                        onChange={handleFilterChange}
                        label="Resultados"
                        renderValue={(selected) => (
                          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                            {selected.map((value) => (
                              <Chip key={value} label={value} size="small" />
                            ))}
                          </Box>
                        )}
                      >
                        {filterOptions.resultados && filterOptions.resultados.map((option) => (
                          <MenuItem key={option} value={option}>{option}</MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Filtros de pesquisa (Número, Keyword, Modo de pesquisa) */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      label="Pesquisar Número de Processo"
                      name="titulo_keyword"
                      value={selectedFilters.titulo_keyword}
                      onChange={handleFilterChange}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <TextField
                      fullWidth
                      label="Pesquisar em Ementa"
                      name="ementa_keyword"
                      value={selectedFilters.ementa_keyword}
                      onChange={handleFilterChange}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={4}>
                    <FormControl fullWidth>
                      <InputLabel id="ementa-search-mode-label">Modo de Pesquisa</InputLabel>
                      <Select
                        labelId="ementa-search-mode-label"
                        name="ementa_search_mode"
                        value={selectedFilters.ementa_search_mode}
                        onChange={handleFilterChange}
                        label="Modo de Pesquisa"
                      >
                        <MenuItem value="Correspondência exata">Correspondência exata</MenuItem>
                        <MenuItem value="Encontrar todas as palavras">Encontrar todas as palavras</MenuItem>
                        <MenuItem value="Encontrar pelo menos uma palavra">Encontrar pelo menos uma palavra</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                </Grid>

                {/* Filtros de data */}
                <Grid container spacing={2} sx={{ mb: 3 }}>
                  <Grid item xs={12} sm={6} md={6}>
                    <DatePicker
                      label="Data Início"
                      format="dd/MM/yyyy"
                      value={selectedFilters.period_start ? new Date(selectedFilters.period_start) : null}
                      onChange={(date) => handleDateChange(date, 'period_start')}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>

                  <Grid item xs={12} sm={6} md={6}>
                    <DatePicker
                      label="Data Fim"
                      format="dd/MM/yyyy"
                      value={selectedFilters.period_end ? new Date(selectedFilters.period_end) : null}
                      onChange={(date) => handleDateChange(date, 'period_end')}
                      renderInput={(params) => <TextField {...params} fullWidth />}
                    />
                  </Grid>
                </Grid>

                {/* Botões de ação */}
                <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-start' }}>
                  <Button 
                    variant="contained" 
                    onClick={applyFilters}
                    disabled={loading}
                    startIcon={<RefreshIcon />}
                    sx={{ 
                      background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                      '&:hover': {
                        background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`,
                      },
                    }}
                  >
                    {loading ? 'Aplicando...' : 'Aplicar Filtros'}
                  </Button>
                  <Button 
                    variant="outlined" 
                    onClick={() => {
                      setSelectedFilters({
                        advogados: [],
                        recorrentes: [],
                        relatores: [],
                        resultados: [],
                        period_start: null,
                        period_end: null,
                        titulo_keyword: '',
                        ementa_keyword: '',
                        ementa_search_mode: 'Correspondência exata',
                      });
                    }}
                  >
                    Limpar Filtros
                  </Button>
                </Box>
              </Box>
            </Collapse>
          </CardContent>
        </Card>
      )}

      {/* Accord Limit Alert */}
      {accordLimitAlert && (
        <Alert 
          severity="warning" 
          sx={{ mb: 3, borderRadius: 2 }}
          onClose={() => {/* Alert will auto-hide */}}
        >
          Limite máximo de 10 acórdãos atingido. Para selecionar novos acórdãos, remova alguns da lista primeiro.
        </Alert>
      )}

      {/* Status Section */}
      {loading && (
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', my: 4 }}>
          <CircularProgress size={60} thickness={4} />
          <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
            {error ? 'Tentando novamente...' : 'Carregando dados do backend...'}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
            O backend pode demorar alguns segundos para processar grandes volumes de dados
          </Typography>
        </Box>
      )}
      
      {error && !loading && (
        <Alert 
          severity="error" 
          sx={{ mb: 3, borderRadius: 2 }}
          action={
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button color="inherit" size="small" onClick={refreshData}>
                Tentar Novamente
              </Button>
              <Button color="inherit" size="small" onClick={() => setError(null)}>
                Fechar
              </Button>
            </Box>
          }
        >
          {error}
        </Alert>
      )}

      {/* Map Section */}
      {!loading && !error && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Visualização dos Acórdãos
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {/* Upload button */}
                <input
                  accept=".docx,.pdf,.txt"
                  style={{ display: 'none' }}
                  id="map-case-file-upload"
                  type="file"
                  onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    try {
                      setLoading(true);
                      await uploadCaseFile(file);
                      // After upload, overlay the uploaded case immediately (force refresh cache)
                      const uploaded = await fetchUploadedCaseData(true);
                      // Pre-select the decisions most similar to the uploaded case
                      if (onPreselectAccords) {
                        onPreselectAccords(similarCasesToAccords(uploaded));
                      }
                      if (uploaded && uploaded.filtered_data_ids && uploaded.embeddings_2D && uploaded.hover_data) {
                        const idx = 0;
                        setAccords(prev => ([
                          ...prev.filter(a => !a.uploadedCase),
                          {
                            id: uploaded.filtered_data_ids[idx],
                            x: uploaded.embeddings_2D.x[idx],
                            y: uploaded.embeddings_2D.y[idx],
                            result: uploaded.hover_data.acordao_resultado?.[idx] ?? 'Carregado pelo usuário',
                            ementa: uploaded.hover_data.acordao_ementa?.[idx] ?? 'Carregado pelo usuário',
                            penalidade: uploaded.hover_data.acordao_penalidade?.[idx] ?? 'Carregado pelo usuário',
                            baseLegal: uploaded.hover_data.acordao_base?.[idx] ?? 'Carregado pelo usuário',
                            ano: uploaded.hover_data.ano_sessoes?.[idx] ?? 'Carregado pelo usuário',
                            sessao: uploaded.hover_data.sessao_nr_int?.[idx] ?? 'Carregado pelo usuário',
                            processo: uploaded.hover_data.acordao_titulo?.[idx] ?? file.name,
                            uploadedCase: true,
                          }
                        ]));
                      }
                    } catch (err) {
                      console.error('Erro ao carregar caso no mapa:', err);
                      setError('Erro ao carregar caso. Tente novamente.');
                    } finally {
                      setLoading(false);
                      // reset input so same file can be selected again if desired
                      e.target.value = '';
                    }
                  }}
                />
                <label htmlFor="map-case-file-upload">
                  <Button variant="outlined" component="span" startIcon={<CloudUploadIcon />} size="small">
                    Carregar infração
                  </Button>
                </label>
                <Chip 
                  label={`${accords.length} acórdãos encontrados`}
                  color="primary"
                  variant="outlined"
                />
              </Box>
            </Box>
            
            {/* Instructions */}
            <Box 
              sx={{
                mb: 2,
                p: 1.5,
                backgroundColor: alpha(theme.palette.info.main, 0.1),
                border: `1px solid ${alpha(theme.palette.info.main, 0.2)}`,
                borderRadius: 1,
              }}
            >
              <Typography variant="caption" color="info.main" sx={{ fontWeight: 500 }}>
                💡 Dica: Ao carregar uma infração, os 10 acórdãos mais semelhantes a ela são pré-selecionados no grid abaixo. Confirme ou altere a seleção: remova acórdãos do grid e clique nos pontos do gráfico para adicionar outros (máx. 10). Use a roda do mouse ou os controles de zoom para explorar melhor o gráfico.
              </Typography>
            </Box>
            
            <MapComponent accords={accords} onAccordClick={onAccordClick} />
          </CardContent>
        </Card>
      )}

      {/* Selected Accords Grid */}
      {isAuthenticated && (
        <SelectedAccordsGrid
          selectedAccords={selectedAccords}
          onRemoveAccord={onRemoveAccord}
        />
      )}
    </Box>
  );
}

export default Home;